/**
 * AdminProjectDetail — the admin role's single-project detail at
 * `/labeling-overview/:category/:projectId` (mockup `#s-adetail`):
 * pipeline strip, enrollments table (with inline Review & approve), a Pool
 * panel (with an ingest-cases dialog), and a Configuration panel.
 *
 * # Pipeline counts
 * `PipelineStrip` counts demo/phase1/phase2 straight off `Enrollment.phase`.
 * Phase 2 is terminal and there is no phase-3 bucket. Counts are derived
 * regardless of
 * enrollment state (active/pending/blocked all still occupy their phase).
 * "Invited" (eligible, not yet accepted) has no backing data at all —
 * Project carries no invite list and Enrollment only exists once an
 * annotator has accepted — so it's passed as `null` (renders "—", see
 * PipelineStrip's `PipelineCounts` doc) rather than a fabricated 0.
 *
 * The strip's manual-approval gate placement is derived from this project's
 * own `gate_policy.auto_advance_from_phase` (threaded into `PipelineStrip`
 * below) rather than assuming the historical fixed demo→P1→P2 shape — see
 * PipelineStrip's header comment for the derivation.
 *
 * "Current focus" is a UI highlight only (not a fetched value): the phase
 * with the most `active` enrollments, ties broken toward the earliest
 * phase. No active enrollments → no stage highlighted.
 *
 * # Pool + Configuration panels
 * Pool renders `PoolGauge` (`variant="full"`) once `usePoolStatusQuery`
 * resolves; "Ingest cases" opens a `Dialog` embedding `IngestPoolControl`
 * (Task 9). Configuration is `ProjectConfigPanel` (Task 9) — editable
 * GT staff / `email_recipients`, read-only `phase_config` / gates.
 */

import { useCallback, useMemo, useState } from "react";
import { useLabelingRole } from "@/hooks/useLabelingRole";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/design-system/components/react";
import {
  useFactoryProjectsQuery,
  useAgreementCheckMutation,
  useProjectAnalyticsQuery,
  useProjectEnrollmentsQuery,
  usePoolStatusQuery,
} from "../../../../hooks/useFactoryQueries";
import { authService } from "../../../../services/auth";
import type { Batch, Enrollment, EvaluationAnalyticsRow } from "../../../../types/factory";
import { CATEGORIES, type LabelingCategoryKey } from "../../../../utils/labelingCategories";
import { batchSeq } from "../../../../utils/factoryDisplay";
import { defaultFactoryTimingWindow } from "../../../../utils/factoryTimingCsv";
import { FaChevronRight } from "../../../../utils/icons";
import { LoadingPanel, PoolGauge, StatusBadge } from "../primitives";
import { AnalyticsPanel } from "./AnalyticsPanel";
import { EnrollmentsTable } from "./EnrollmentsTable";
import { IngestPoolControl } from "./IngestPoolControl";
import { PipelineStrip, type PipelineCounts, type PipelineStageKey } from "./PipelineStrip";
import { ProjectConfigPanel } from "./ProjectConfigPanel";

function pipelineCounts(enrollments: Enrollment[]): PipelineCounts {
  return {
    invited: null,
    demo: enrollments.filter((e) => e.phase === 0).length,
    phase1: enrollments.filter((e) => e.phase === 1).length,
    phase2: enrollments.filter((e) => e.phase === 2).length,
  };
}

/** The stage with the most `active` enrollments — a display highlight, not
 *  a fetched value. Undefined (no highlight) when nobody is active yet. */
function currentFocusStage(enrollments: Enrollment[]): PipelineStageKey | undefined {
  const active = enrollments.filter((e) => e.state === "active");
  if (active.length === 0) return undefined;

  const byStage: Record<Exclude<PipelineStageKey, "invited">, number> = {
    demo: 0,
    phase1: 0,
    phase2: 0,
  };
  for (const e of active) {
    if (e.phase === 0) byStage.demo += 1;
    else if (e.phase === 1) byStage.phase1 += 1;
    else if (e.phase === 2) byStage.phase2 += 1;
  }
  const order: Array<Exclude<PipelineStageKey, "invited">> = ["demo", "phase1", "phase2"];
  return order.reduce((best, stage) => (byStage[stage] > byStage[best] ? stage : best), order[0]);
}

interface AdminProjectDetailProps {
  categoryKey: LabelingCategoryKey;
  projectId: string;
}

export function AdminProjectDetail({ categoryKey, projectId }: AdminProjectDetailProps) {
  const navigate = useNavigate();
  const role = useLabelingRole();
  const meta = CATEGORIES.find((c) => c.key === categoryKey)!;
  const [ingestOpen, setIngestOpen] = useState(false);
  const [timingWindow, setTimingWindow] = useState(defaultFactoryTimingWindow);

  const projectsQuery = useFactoryProjectsQuery();
  const enrollmentsQuery = useProjectEnrollmentsQuery(projectId);
  const poolQuery = usePoolStatusQuery(projectId);
  const analyticsQuery = useProjectAnalyticsQuery(
    projectId,
    timingWindow.startDate,
    timingWindow.endDate,
  );
  const agreementCheck = useAgreementCheckMutation();
  const runEvaluation = useCallback(
    (row: EvaluationAnalyticsRow) =>
      agreementCheck.mutateAsync({
        pid: row.project_id,
        annotator: row.annotator,
        batchId: row.batch_id,
      }),
    [agreementCheck],
  );
  // Migrated enrollments (e.g. us3d) carry no factory current_batch — their
  // real assignments live as per-user project grants (us3d-phaseNN-batchNN).
  // Pull them from the admin user list and map annotator → their worklists for
  // this project so the roster can show batches + progress + open links.
  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => authService.getAdminUsers(),
  });
  const grantedWorklists = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const u of usersQuery.data?.users ?? []) {
      const wls = (u.allowed_projects ?? []).filter((p) => p.startsWith(`${projectId}-`));
      if (wls.length) map[u.user_id] = [...wls].sort();
    }
    return map;
  }, [usersQuery.data, projectId]);

  const project = useMemo(
    () => projectsQuery.data?.find((p) => p.project_id === projectId),
    [projectsQuery.data, projectId],
  );
  const enrollments = useMemo(() => enrollmentsQuery.data ?? [], [enrollmentsQuery.data]);
  const demoBatches = useMemo(
    () =>
      enrollments
        .flatMap((enrollment) => enrollment.batches ?? [])
        .filter((batch): batch is Batch => batch.phase === 0)
        .sort((a, b) => a.created_at.localeCompare(b.created_at)),
    [enrollments],
  );

  const counts = useMemo(() => pipelineCounts(enrollments), [enrollments]);
  const focusStage = useMemo(() => currentFocusStage(enrollments), [enrollments]);

  const stageMeta = project
    ? {
        demo: `${project.phase_config.demo_total} · ${project.phase_config.demo_unique}+${project.phase_config.demo_gt} GT`,
        phase1: `${project.phase_config.working_total} · ${project.phase_config.working_unique}+${project.phase_config.working_overlap} peer`,
        phase2: `${project.phase_config.phase2_total ?? 100} unique · terminal`,
      }
    : undefined;

  const handleBack = () => navigate(`/labeling-overview/${categoryKey}`);

  const handleIngest = () => setIngestOpen(true);
  const openWorklist = (worklistId: string) =>
    navigate(`/labeling?project=${encodeURIComponent(worklistId)}`);

  if (projectsQuery.isLoading) {
    return (
      <div className="h-full overflow-auto text-white page-premium">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <div className="rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted">
            Loading project…
          </div>
        </div>
      </div>
    );
  }

  if (projectsQuery.error) {
    return (
      <div className="h-full overflow-auto text-white page-premium">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <button
            type="button"
            onClick={handleBack}
            className="mb-4 text-xs text-fg-muted transition hover:text-fg"
          >
            ← {meta.name} · control room
          </button>
          <div className="rounded border border-warn/30 bg-warn-tint p-8 text-center text-sm text-warn">
            Failed to load project
          </div>
        </div>
      </div>
    );
  }

  // Only reachable once the projects query has succeeded — a fetch failure
  // is handled above and must never be reported as "not found" (that would
  // hide a real backend/network error behind a wrong-but-plausible message).
  if (!project) {
    return (
      <div className="h-full overflow-auto text-white page-premium">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <button
            type="button"
            onClick={handleBack}
            className="mb-4 text-xs text-fg-muted transition hover:text-fg"
          >
            ← {meta.name} · control room
          </button>
          <div className="rounded border border-warn/30 bg-warn-tint p-8 text-center text-sm text-warn">
            Project &quot;{projectId}&quot; not found.
          </div>
        </div>
      </div>
    );
  }

  const groundTruthWorklist = project.gt_worklist_project_id || `${project.project_id}-gt-shared`;
  const usesStandingGtPool = groundTruthWorklist === `${project.project_id}-gt-shared`;
  const gtPoolReady = (poolQuery.data?.gt ?? 0) >= project.phase_config.demo_gt;

  return (
    <div className="h-full overflow-auto text-white page-premium">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <button
          type="button"
          onClick={handleBack}
          className="mb-4 text-xs text-fg-muted transition hover:text-fg"
        >
          ← {meta.name} · control room
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold text-fg">{project.name}</h1>
            <span className="font-mono text-sm text-fg-muted">{project.project_id}</span>
            <StatusBadge tone="sig">Admin</StatusBadge>
          </div>
          <div className="mt-1.5 text-sm text-fg-muted">
            {project.task} · {enrollments.length} enrollment
            {enrollments.length === 1 ? "" : "s"} ·{" "}
            {(project.gt_annotators?.length ?? (project.gt_annotator ? 1 : 0)) > 0
              ? `${project.gt_annotators?.length ?? 1} golden-GT staff assigned`
              : "no GT staff assigned"}
          </div>
        </div>

        <div className="rounded border border-border/50 bg-surface p-5">
          <div className="mb-3 flex items-center gap-2.5">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-dim">
              Pipeline
            </span>
            <span className="flex-1" />
            <span className="text-[11.5px] text-fg-faint">annotators per stage</span>
          </div>
          {enrollmentsQuery.isLoading ? (
            <div className="p-6 text-center text-sm text-fg-muted">Loading pipeline…</div>
          ) : (
            <PipelineStrip
              counts={counts}
              meta={stageMeta}
              currentStage={focusStage}
              autoAdvanceFromPhase={project.gate_policy.auto_advance_from_phase}
            />
          )}
        </div>

        <div className="mt-4 rounded border border-border/50 bg-surface" data-testid="demo-batches">
          <div className="flex items-center gap-3 border-b border-border/50 px-5 py-3.5">
            <h3 className="text-sm font-semibold text-fg">Demo batches</h3>
            <StatusBadge tone="ghost">20 cases each · 15 unique + 5 GT</StatusBadge>
            <span className="ml-auto text-[11px] text-fg-faint">
              Configured reference and every annotator demo
            </span>
          </div>
          <div className="divide-y divide-border/50">
            <button
              type="button"
              disabled={usesStandingGtPool && !gtPoolReady}
              onClick={() => openWorklist(groundTruthWorklist)}
              className="flex w-full items-center gap-4 px-5 py-3 text-left transition hover:bg-white/2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <StatusBadge tone="success">GT</StatusBadge>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs text-fg">{groundTruthWorklist}</div>
                <div className="text-[11px] text-fg-muted">Configured comparison reference</div>
              </div>
              {usesStandingGtPool && !gtPoolReady ? (
                <span className="text-[11px] text-warn">needs GT ingest</span>
              ) : (
                <FaChevronRight className="h-3 w-3 text-fg-muted" />
              )}
            </button>
            {demoBatches.map((batch) => (
              <button
                key={batch.batch_id}
                type="button"
                onClick={() => openWorklist(batch.worklist_project_id)}
                className="flex w-full items-center gap-4 px-5 py-3 text-left transition hover:bg-white/2"
              >
                <StatusBadge tone={batch.finalized ? "ghost" : "sig"}>
                  {batch.finalized ? "scored" : "active"}
                </StatusBadge>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-fg">
                    <span className="font-mono">Demo batch {batchSeq(batch.batch_id)}</span>
                    <span className="ml-2 text-fg-muted">{batch.annotator}</span>
                  </div>
                  <div className="font-mono text-[11px] text-fg-faint">
                    {batch.worklist_project_id} ·{" "}
                    {batch.unique_uids.length + batch.shared_uids.length} cases
                  </div>
                </div>
                <FaChevronRight className="h-3 w-3 text-fg-muted" />
              </button>
            ))}
            {demoBatches.length === 0 && (
              <div className="px-5 py-3 text-xs text-fg-faint">
                No annotator demo batches have been created yet.
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-4 lg:flex-row">
          <div className="lg:flex-[1.6]">
            {enrollmentsQuery.isLoading ? (
              <div className="rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted">
                Loading enrollments…
              </div>
            ) : enrollmentsQuery.error ? (
              <div className="rounded border border-warn/30 bg-warn-tint p-8 text-center text-sm text-warn">
                Failed to load enrollments
              </div>
            ) : (
              // key={projectId}: EnrollmentsTable's live-scorecard /
              // checking / error state is keyed by annotator alone, and
              // /labeling-overview/:category/:projectId renders the same
              // element on a param change, so React reconciles rather than
              // remounting. Without this, navigating A -> B shows A's score
              // on an annotator enrolled in both.
              <EnrollmentsTable
                key={projectId}
                projectId={projectId}
                enrollments={enrollments}
                grantedWorklists={grantedWorklists}
              />
            )}
          </div>

          <div className="flex flex-col gap-4 lg:flex-1">
            <div className="rounded border border-border/50 bg-surface p-5">
              <div className="mb-3 flex items-center gap-2.5">
                <h3 className="text-sm font-semibold text-fg">Pool</h3>
                <span className="flex-1" />
                <button
                  type="button"
                  onClick={handleIngest}
                  className="rounded border border-border-strong px-3 py-1.5 text-xs font-semibold text-fg transition hover:bg-white/[0.04]"
                >
                  Ingest cases
                </button>
              </div>
              {!poolQuery.data ? (
                <div className="text-xs text-fg-faint">
                  {poolQuery.error ? "Failed to load pool status" : "Pool pending…"}
                </div>
              ) : (
                <PoolGauge
                  available={poolQuery.data.available}
                  awaitingSecond={poolQuery.data.awaiting_second}
                  gt={poolQuery.data.gt}
                  variant="full"
                />
              )}
            </div>

            <ProjectConfigPanel key={project.project_id} project={project} />
          </div>
        </div>

        {role === "admin" && (
          <div className="mt-4">
            {analyticsQuery.isLoading ? (
              <LoadingPanel label="Loading production analytics…" />
            ) : analyticsQuery.error || !analyticsQuery.data ? (
              <div className="rounded border border-warn/30 bg-warn-tint p-6 text-center text-sm text-warn">
                Failed to load production analytics
              </div>
            ) : (
              <AnalyticsPanel
                analytics={analyticsQuery.data}
                scope="project"
                timingWindow={timingWindow}
                onTimingWindowChange={setTimingWindow}
                onRunEvaluation={runEvaluation}
              />
            )}
          </div>
        )}
      </div>

      <Dialog open={ingestOpen} onOpenChange={setIngestOpen}>
        <DialogContent className="max-w-xl bg-surface border-border-strong">
          <DialogHeader>
            <DialogTitle className="text-fg">Ingest cases — {project.project_id}</DialogTitle>
          </DialogHeader>
          <IngestPoolControl
            projectId={project.project_id}
            workflow={project.workflow}
            sourceCohortId={project.source_cohort_id}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminProjectDetail;
